import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import EmptyPage from "components/common/EmptyPage";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import GestionTable from "components/pages/Gestion/GestionTable";
import { leagueCustomerFinancialSiloBalancesRoute } from "network/api-routes";
import { accountNetBalance, formatFinancialAmount } from "./financial-helpers";
import { financialOverviewColumnKeys } from "./financial-overview-columns";
import { useFinancialPagedList } from "./useFinancialPagedList";

const GestionFinancialOverview = ({
  customerId,
  user,
  reloadKey,
  onError,
  incomeSummary,
  onOpenAccount,
  accountSummary,
}) => {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [listTab, setListTab] = useState("entity");
  const [showZeroBalance, setShowZeroBalance] = useState(true);

  // Filtering, search and the saldo ordering happen server-side.
  const balances = useFinancialPagedList({
    customerId,
    user,
    route: leagueCustomerFinancialSiloBalancesRoute,
    search,
    filters: { type: listTab, include_zero: showZeroBalance ? 1 : 0 },
    initialPageSize: 10,
    reloadKey,
    onError,
  });

  const overview = incomeSummary?.overview || {};
  const currency =
    overview.currency || balances.results[0]?.currency_code || "";

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {accountSummary}
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <RevaText variant="overline" color="text.secondary">
              {intl.formatMessage({ id: "gestion.financials.totalCollected" })}
            </RevaText>
            <RevaText variant="h5">
              {formatFinancialAmount(overview.total_collected, currency)}
            </RevaText>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <RevaText variant="overline" color="text.secondary">
              {intl.formatMessage({ id: "gestion.financials.totalPending" })}
            </RevaText>
            <RevaText variant="h5">
              {formatFinancialAmount(overview.total_pending, currency)}
            </RevaText>
          </CardContent>
        </Card>
      </Stack>

      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
          alignItems={{ md: "center" }}
        >
          <Stack direction="row" spacing={1}>
            <Chip
              label={intl.formatMessage({ id: "gestion.financials.teamsTab" })}
              color={listTab === "entity" ? "primary" : "default"}
              onClick={() => setListTab("entity")}
            />
            <Chip
              label={intl.formatMessage({
                id: "gestion.financials.playersTab",
              })}
              color={listTab === "individual" ? "primary" : "default"}
              onClick={() => setListTab("individual")}
            />
          </Stack>
          <TextField
            size="small"
            label={intl.formatMessage({ id: "gestion.financials.search" })}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showZeroBalance}
                onChange={(event) => setShowZeroBalance(event.target.checked)}
              />
            }
            label={intl.formatMessage({
              id: "gestion.financials.showZeroBalance",
            })}
          />
        </Stack>

        {balances.totalResults === 0 ? (
          balances.loading ? null : (
            <EmptyPage
              text={intl.formatMessage({ id: "gestion.financials.noAccounts" })}
            />
          )
        ) : (
          <>
            <GestionTable
              onRowClick={(account) => onOpenAccount(account)}
              headers={financialOverviewColumnKeys(listTab).map((key) => ({
                key,
                title:
                  key === "accountName"
                    ? intl.formatMessage({
                        id: "gestion.financials.accountName",
                      })
                    : key === "document"
                      ? intl.formatMessage({ id: "gestion.players.document" })
                      : intl.formatMessage({
                          id: "gestion.financials.balance",
                        }),
                align: key === "balance" ? "right" : undefined,
              }))}
              rows={balances.results.map((account) => ({
                ...account,
                id: `${account.type}-${account.target_id}`,
                accountName: account.target_name || account.target_id,
                document: account.document_number || "—",
                balance: formatFinancialAmount(
                  accountNetBalance(account),
                  account.currency_code || currency,
                ),
              }))}
            />
            <GestionListPagination
              page={balances.page}
              pageSize={balances.pageSize}
              totalPages={balances.totalPages}
              totalResults={balances.totalResults}
              onPageChange={balances.setPage}
              onPageSizeChange={balances.setPageSize}
              disabled={balances.loading}
            />
          </>
        )}
      </Box>
    </Stack>
  );
};

export default GestionFinancialOverview;
